import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import ChoiceRevealOnScroll from "../shared/ChoiceRevealOnScroll.jsx";
import { fadeUpVariants, staggerContainer } from "../shared/animations.js";

const FIELD_PROOF_SCREENSHOT = "/images/proof/ozexteriors.png";

const FieldProofScreenshot = ({ className = "" }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div
        className={`flex aspect-[16/10] w-full items-center justify-center rounded-lg border border-dashed border-amber-500/25 bg-amber-500/[0.03] px-6 text-center ${className}`}
        data-testid="field-proof-screenshot"
      >
        <p className="text-sm font-mono text-gray-500">Screenshot coming soon</p>
      </div>
    );
  }

  return (
    <div
      className={`relative aspect-[16/10] w-full overflow-hidden rounded-lg border border-white/10 bg-black/50 shadow-2xl shadow-black/40 ${className}`}
      data-testid="field-proof-screenshot"
    >
      <img
        src={FIELD_PROOF_SCREENSHOT}
        alt="Oz Exteriors digital employee results"
        className="h-full w-full object-cover object-right object-top"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const FieldProofSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const metrics = [
    {
      value: "+6",
      label: "Additional appointments per week",
      sub: "Started after digital employee went live",
      highlight: true,
    },
    {
      value: "9 sec",
      label: "Response time",
      sub: "vs 30–40 min office",
      highlight: false,
    },
    {
      value: "100%",
      label: "After-hours capture",
      sub: "Outside business hours",
      highlight: false,
    },
  ];

  return (
    <section
      id="field-proof"
      className="relative overflow-hidden py-16 sm:py-20 lg:py-24 border-y border-amber-500/15 bg-[#060a12]"
      data-testid="field-proof-section"
      ref={ref}
    >
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(245,158,11,0.07)_0%,transparent_45%,rgba(20,184,166,0.05)_100%)] pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(-45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)",
          backgroundSize: "12px 12px",
        }}
      />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
        <ChoiceRevealOnScroll>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
            <div>
              <div className="inline-flex items-center gap-3 mb-3">
                <span
                  className="inline-block badge-mono text-[11px] sm:text-xs font-bold tracking-[0.35em] text-amber-950 bg-amber-400 px-4 py-2 rounded-sm shadow-lg shadow-amber-500/20 rotate-[-1deg]"
                  data-testid="field-proof-case-label"
                >
                  CASE STUDY
                </span>
                <span className="hidden sm:inline text-[10px] font-mono text-amber-500/50 tracking-widest">POST-DEPLOYMENT</span>
              </div>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight text-white"
                data-testid="field-proof-headline"
              >
                <span className="text-amber-400">+6 Additional Appointments</span>
                <br />
                Per Week
              </h2>
              <p className="text-base sm:text-lg text-gray-400 font-body leading-relaxed mt-4 max-w-2xl">
                After connecting a digital employee,{" "}
                <a
                  href="https://ozexteriors.com.au/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-white font-semibold hover:text-amber-400 transition-colors"
                >
                  Oz Exteriors
                </a>{" "}
                started booking six more qualified appointments on the calendar every week.
              </p>
            </div>
            <p className="text-xs font-mono text-gray-500 sm:text-right max-w-[14rem] sm:pb-1">
              Real company · Australia · Weekly uplift after go-live
            </p>
          </div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10"
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {metrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                variants={fadeUpVariants}
                className={`relative rounded-xl p-5 sm:p-6 border ${
                  metric.highlight
                    ? "border-amber-500/40 bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent ring-1 ring-amber-500/20"
                    : "border-white/[0.08] bg-white/[0.02]"
                }`}
                data-testid={index === 0 ? "field-proof-stat" : undefined}
              >
                {metric.highlight && (
                  <span className="absolute -top-2.5 left-4 badge-mono text-[9px] text-amber-400 bg-[#060a12] border border-amber-500/30 px-2 py-0.5 rounded-full">
                    KEY RESULT
                  </span>
                )}
                <div
                  className={`text-4xl sm:text-5xl font-bold font-mono mb-1 leading-none ${
                    metric.highlight ? "text-amber-400" : "text-teal-400"
                  }`}
                >
                  {metric.value}
                </div>
                <div className="text-sm font-semibold text-white mb-0.5">{metric.label}</div>
                <div className="text-[11px] font-mono text-gray-500">{metric.sub}</div>
              </motion.div>
            ))}
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            <div className="lg:col-span-5 space-y-5">
              <div className="flex items-start gap-4 p-5 rounded-xl border border-white/[0.08] bg-white/[0.02]">
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-amber-950 font-bold text-lg shadow-lg shadow-amber-500/20">
                  OE
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-mono text-amber-400/80 tracking-widest mb-1">CLIENT</p>
                  <p className="text-lg font-bold text-white leading-tight">
                    <a
                      href="https://ozexteriors.com.au/"
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-amber-400 transition-colors"
                    >
                      Oz Exteriors
                    </a>
                  </p>
                  <p className="text-sm text-gray-500 font-body mt-1">Australia · Inbound lead handling</p>
                </div>
              </div>

              <p className="text-base text-gray-400 font-body leading-relaxed" data-testid="field-proof-intro">
                After connecting a digital employee,{" "}
                <a
                  href="https://ozexteriors.com.au/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-white font-semibold hover:text-amber-400 transition-colors"
                >
                  Oz Exteriors
                </a>{" "}
                began booking{" "}
                <span className="text-amber-400 font-semibold">+6 additional qualified appointments per week</span> — captured in under{" "}
                <span className="text-teal-400 font-semibold">9 seconds</span>, including outside business hours, while the office workflow relied on a typical{" "}
                <span className="text-white font-semibold">30–40 minute</span> callback.
              </p>

              <div
                className="rounded-xl overflow-hidden border border-white/[0.08]"
                data-testid="field-proof-thesis"
              >
                <div className="grid grid-cols-2 text-center text-[10px] sm:text-xs font-mono tracking-wider">
                  <div className="py-3 px-2 bg-red-500/10 text-red-400/90 border-b border-r border-white/[0.06]">
                    <span className="block text-[9px] text-gray-600 mb-1">BEFORE</span>
                    <span className="line-through opacity-80">30–40 min callback</span>
                  </div>
                  <div className="py-3 px-2 bg-teal-500/10 text-teal-400 border-b border-white/[0.06]">
                    <span className="block text-[9px] text-gray-600 mb-1">AFTER</span>
                    <span className="font-semibold">9 sec digital employee</span>
                  </div>
                </div>
                <div className="py-3 px-4 text-center bg-amber-500/[0.08] border-t border-amber-500/20">
                  <span className="text-xs sm:text-sm font-mono text-amber-400 font-semibold">Outcome: +6 additional appointments per week · after go-live</span>
                </div>
              </div>

              <p className="text-[11px] text-gray-600 font-mono" data-testid="field-proof-disclaimer">
                Example from one live deployment — weekly uplift varies by inbound volume and vertical.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="relative p-3 sm:p-4 rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 rotate-[0.5deg] lg:rotate-1 shadow-xl shadow-black/30">
                <div className="absolute -top-3 left-6 badge-mono text-[10px] text-gray-400 bg-[#060a12] border border-white/10 px-3 py-1 rounded-full">
                  EVIDENCE · POST-DEPLOYMENT
                </div>
                <FieldProofScreenshot className="rounded-md" />
                <p className="mt-3 text-center text-[10px] font-mono text-gray-600 tracking-wide">
                  Calendar &amp; qualification logs · Oz Exteriors deployment
                </p>
              </div>
            </div>
          </div>
        </ChoiceRevealOnScroll>
      </div>
    </section>
  );
};

export default FieldProofSection;
